/**
 * DashboardPage — Main financial overview
 *
 * Layout (mobile, single column):
 *   1. SpendableCard — Hero: spendable balance + daily allowance
 *   2. BudgetGauges — 50/30/20 SVG semi-circle progress gauges
 *   3. MoneyOnFire — Late fees & interest charges (hidden if $0)
 *   4. Upcoming Bills — Next 5 unpaid bills sorted by due date
 *   5. Recent Activity — Last 10 transactions with bucket color dots
 *   6. Sync Status — Last sync time + manual refresh button
 *
 * Phase 7 additions:
 *   - Offline fallback: yellow card with cached data when network fails
 *   - localStorage snapshot after successful loads
 *   - Badge API: unpaid bill total shown as app icon badge
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserConfig, getActiveBills, getBudgetPeriod, getBudgetHistory, getActiveGoals, getAlerts, markAlertRead, markAllAlertsRead, getTransactionsByMonth, getCategoryOverrides } from '../services/db';
import { getRecentTransactions } from '../services/db';
import { callTriggerSync } from '../services/functions';
import { calculateSpendable } from '../utils/spendable';
import { calculateBudget, BUCKET_META, BUCKETS, getCategoryBucket } from '../utils/budget';
import { formatCurrency, formatDate, ordinalDay, getMonthKey, formatMonthLabel } from '../utils/format';
import { getDaysUntilPayday, getBillUrgency } from '../utils/dates';
import { saveOfflineSnapshot, getOfflineSnapshot } from '../services/offlineCache';
import SpendableCard from '../components/SpendableCard';
import BudgetGauge from '../components/BudgetGauge';
import MoneyOnFire from '../components/MoneyOnFire';
import { SkeletonLine, SkeletonCard } from '../components/Skeleton';
import CashFlowChart from '../components/CashFlowChart';
import SpendingTrends from '../components/SpendingTrends';
import GoalsSummary from '../components/GoalsSummary';
import AlertsCard from '../components/AlertsCard';
import theme from '../utils/theme';

export default function DashboardPage({ setPage }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [spendableResult, setSpendableResult] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [fireItems, setFireItems] = useState([]);
  const [recentTxns, setRecentTxns] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineData, setOfflineData] = useState(null);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [goals, setGoals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [budgetMonthLabel, setBudgetMonthLabel] = useState(null);
  const [error, setError] = useState(null);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /**
   * Load all dashboard data from Firestore.
   */
  const loadData = useCallback(async () => {
    try {
      const monthKey = getMonthKey();
      const [userConfig, userBills, budgetPeriod, txns, history, userGoals, userAlerts, monthTxns, catOverrides] = await Promise.all([
        getUserConfig(user.id),
        getActiveBills(user.id),
        getBudgetPeriod(user.id, monthKey),
        getRecentTransactions(user.id, 10),
        getBudgetHistory(user.id, 6),
        getActiveGoals(user.id),
        getAlerts(user.id, 10),
        getTransactionsByMonth(user.id, monthKey),
        getCategoryOverrides(user.id),
      ]);

      setConfig(userConfig);
      setBills(userBills);
      setRecentTxns(txns);
      setBudgetHistory(history);
      setGoals(userGoals);
      setAlerts(userAlerts);

      // Compute spendable balance
      let spendable = null;
      if (userConfig?.payday_anchor) {
        const result = calculateSpendable({
          checkingBalance: userConfig.checking_balance || 0,
          bills: userBills,
          paydayAnchor: userConfig.payday_anchor,
        });
        setSpendableResult(result);
        spendable = result;
      }

      // Budget gauges — compute directly from this month's transactions (same as BudgetPage)
      const income = userConfig?.conservative_income || 8030;
      const overrides = {};
      for (const [name, bucket] of Object.entries(catOverrides || {})) overrides[name.toLowerCase()] = bucket;

      let needActual = 0, wantActual = 0, savingsActual = 0;
      for (const txn of (monthTxns || [])) {
        if (txn.is_credit) continue;
        const bucket = txn.bucket || getCategoryBucket(txn.category, overrides);
        if (bucket === 'need') needActual += txn.amount || 0;
        else if (bucket === 'savings') savingsActual += txn.amount || 0;
        else wantActual += txn.amount || 0;
      }

      const hasCurrentData = needActual + wantActual + savingsActual > 0;

      if (hasCurrentData) {
        setBudgetData(calculateBudget({ income, needActual, wantActual, savingsActual }));
        setBudgetMonthLabel(null);
        if (budgetPeriod?.money_on_fire_items?.length > 0) setFireItems(budgetPeriod.money_on_fire_items);
        else setFireItems([]);
      } else {
        // No transactions this month — fall back to most recent history month with data
        const fallback = (history || [])
          .filter(h => h && ((h.need_actual || 0) + (h.want_actual || 0) + (h.savings_actual || 0)) > 0)
          .sort((a, b) => (b.monthKey > a.monthKey ? 1 : -1))[0] || null;

        if (fallback) {
          setBudgetData(calculateBudget({
            income,
            needActual: fallback.need_actual || 0,
            wantActual: fallback.want_actual || 0,
            savingsActual: fallback.savings_actual || 0,
          }));
          setBudgetMonthLabel(formatMonthLabel(fallback.monthKey));
          if (fallback.money_on_fire_items?.length > 0) setFireItems(fallback.money_on_fire_items);
          else setFireItems([]);
        } else {
          // Last resort — estimate from bills
          const needBills = userBills.filter(b => b.bucket === 'need');
          const wantBills = userBills.filter(b => b.bucket === 'want');
          const needTotal = needBills.reduce((s, b) => s + (b.amount || 0), 0);
          const wantTotal = wantBills.reduce((s, b) => s + (b.amount || 0), 0);
          setBudgetData(calculateBudget({ income, needActual: needTotal, wantActual: wantTotal, savingsActual: 0 }));
          setBudgetMonthLabel(null);
          setFireItems([]);
        }
      }

      // Save offline snapshot
      const unpaidBills = userBills.filter(b => !b.current_cycle_paid);
      const unpaidTotal = unpaidBills.reduce((s, b) => s + (b.amount || 0), 0);
      saveOfflineSnapshot({
        checkingBalance: userConfig?.checking_balance || 0,
        spendable: spendable?.spendable || 0,
        dailyAllowance: spendable?.dailyAllowance || 0,
        unpaidBillsCount: unpaidBills.length,
        unpaidBillsTotal: unpaidTotal,
      });

      // App Badge — show unpaid bills dollar total
      updateAppBadge(unpaidTotal);
    } catch (err) {
      console.error('Dashboard load failed:', err);

      // If offline, try to show cached data
      if (!navigator.onLine) {
        const cached = getOfflineSnapshot();
        if (cached) {
          setOfflineData(cached);
        }
      } else {
        setError(err.message || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Handle manual sync button press.
   */
  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await callTriggerSync();
      setSyncMessage({
        type: 'success',
        text: `Synced! ${result.transactions} transactions, ${result.billsMatched ?? 0} bills matched`,
      });
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncMessage({
        type: 'error',
        text: `Sync failed: ${err.message}`,
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    // Show offline cached data while loading if offline
    if (isOffline) {
      const cached = offlineData || getOfflineSnapshot();
      if (cached) {
        return (
          <div className="dashboard">
            <div className="offline-card">
              <div className="offline-card-title">You're offline</div>
              <div className="offline-card-body">
                <p>Spendable: <strong>{formatCurrency(cached.spendable)}</strong></p>
                <p>Daily allowance: <strong>{formatCurrency(cached.dailyAllowance)}</strong></p>
                <p>{cached.unpaidBillsCount} unpaid bills ({formatCurrency(cached.unpaidBillsTotal)})</p>
              </div>
              <div className="offline-card-meta">
                Last updated: {cached.savedAt ? new Date(cached.savedAt).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          </div>
        );
      }
    }
    return (
      <div className="dashboard">
        <SkeletonCard dark>
          <SkeletonLine className="skeleton-line--small" />
          <SkeletonLine className="skeleton-line--hero" />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SkeletonLine className="skeleton-line--short" />
            <SkeletonLine className="skeleton-line--short" />
          </div>
        </SkeletonCard>
        <SkeletonCard>
          <SkeletonLine className="skeleton-line--tall" />
          <SkeletonLine className="skeleton-line--medium" />
        </SkeletonCard>
        <div className="skeleton-gauges-row">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-gauge-card">
              <SkeletonLine className="skeleton-line--tall" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </div>
        <SkeletonCard>
          <SkeletonLine className="skeleton-line--medium" />
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-row">
              <SkeletonLine className="skeleton-line--medium" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </SkeletonCard>
        <SkeletonCard>
          <SkeletonLine className="skeleton-line--medium" />
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-row">
              <SkeletonLine className="skeleton-line--medium" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    );
  }

  // Error state (only when online — offline uses cached data fallback)
  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => { setError(null); setLoading(true); loadData(); }}>
          Retry
        </button>
      </div>
    );
  }

  // Not configured yet — show setup prompt
  if (!config?.payday_anchor) {
    return (
      <div>
        <div className="spendable-card">
          <div className="spendable-label">Spendable until next payday</div>
          <div className="spendable-amount">—</div>
          <div className="spendable-meta"><span>Payday not configured</span></div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👋</div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>One-time setup needed</div>
          <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.25rem' }}>
            Tell us when you get paid and connect your bank — that's it.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <button className="btn btn-primary" onClick={() => setPage('settings')}>
              Set Up Payday &amp; Connect Bank
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntilPayday(config.payday_anchor);
  const upcomingBills = bills
    .filter(b => !b.current_cycle_paid)
    .sort((a, b) => (a.due_day || 0) - (b.due_day || 0))
    .slice(0, 5);

  const lastSync = config.monarch_last_sync?.toDate
    ? config.monarch_last_sync.toDate()
    : config.monarch_last_sync
      ? new Date(config.monarch_last_sync)
      : null;

  return (
    <div className="dashboard">
      {/* Offline indicator */}
      {isOffline && (
        <div className="offline-banner">
          Offline — showing cached data
        </div>
      )}

      {/* Sync status message */}
      {syncMessage && (
        <div className={`alert alert-${syncMessage.type}`}>{syncMessage.text}</div>
      )}

      {/* ===== 1. HERO: Spendable Balance ===== */}
      <SpendableCard result={spendableResult} daysUntil={daysUntil} />

      {/* ===== 1.5. CASH FLOW FORECAST ===== */}
      {config && spendableResult && (
        <CashFlowChart
          checkingBalance={config.checking_balance || 0}
          bills={bills}
          paydayAnchor={config.payday_anchor}
          conservativeIncome={config.conservative_income || 8030}
        />
      )}

      {/* ===== 1.7. ALERTS ===== */}
      <AlertsCard
        alerts={alerts}
        onMarkRead={async (alertId) => {
          setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
          await markAlertRead(user.id, alertId);
        }}
        onMarkAllRead={async () => {
          setAlerts(prev => prev.map(a => ({ ...a, read: true })));
          await markAllAlertsRead(user.id);
        }}
      />

      {/* ===== 2. 50/30/20 Budget Gauges ===== */}
      {budgetData && (
        <>
        {budgetMonthLabel && (
          <div style={{ textAlign: 'center', fontSize: '0.72rem', color: theme.textMuted, marginBottom: '0.25rem' }}>
            Showing {budgetMonthLabel} — no data yet for this month
          </div>
        )}
        <div className="gauges-row">
          <BudgetGauge
            label="Needs" emoji={BUCKET_META[BUCKETS.NEED].emoji} bucket="need"
            percent={budgetData.buckets[BUCKETS.NEED].percent} target="50%"
            actual={budgetData.buckets[BUCKETS.NEED].actual}
            budget={budgetData.buckets[BUCKETS.NEED].target}
            status={budgetData.buckets[BUCKETS.NEED].status}
          />
          <BudgetGauge
            label="Wants" emoji={BUCKET_META[BUCKETS.WANT].emoji} bucket="want"
            percent={budgetData.buckets[BUCKETS.WANT].percent} target="30%"
            actual={budgetData.buckets[BUCKETS.WANT].actual}
            budget={budgetData.buckets[BUCKETS.WANT].target}
            status={budgetData.buckets[BUCKETS.WANT].status}
          />
          <BudgetGauge
            label="Savings" emoji={BUCKET_META[BUCKETS.SAVINGS].emoji} bucket="savings"
            percent={budgetData.buckets[BUCKETS.SAVINGS].percent} target="20%"
            actual={budgetData.buckets[BUCKETS.SAVINGS].actual}
            budget={budgetData.buckets[BUCKETS.SAVINGS].target}
            status={budgetData.buckets[BUCKETS.SAVINGS].status}
          />
        </div>
        </>
      )}

      {/* ===== 2.5. Money on Fire ===== */}
      <MoneyOnFire items={fireItems} />

      {/* ===== 2.7. Spending Trends ===== */}
      {budgetHistory.length >= 2 && (
        <SpendingTrends budgetHistory={budgetHistory} />
      )}

      {/* ===== 3.5. Savings Goals Summary ===== */}
      <GoalsSummary goals={goals} onNavigate={() => setPage('goals')} />

      {/* ===== 4. Upcoming Bills ===== */}
      <div className="card">
        <h3>Upcoming Bills</h3>
        {upcomingBills.length === 0 ? (
          <p className="empty-state">All bills paid this cycle! 🎉</p>
        ) : (
          upcomingBills.map(bill => {
            const urgency = getBillUrgency(bill.due_day);
            return (
              <div key={bill.id} className={`bill-row ${bill.bucket || 'need'}`}>
                <div className="bill-info">
                  <div className="bill-name">{bill.name}</div>
                  <div className={`bill-due ${urgency}`}>
                    Due {ordinalDay(bill.due_day)}
                    {bill.auto_pay && ' · Auto'}
                  </div>
                </div>
                <div className="bill-amount">{formatCurrency(bill.amount)}</div>
              </div>
            );
          })
        )}
        {bills.filter(b => !b.current_cycle_paid).length > 5 && (
          <div style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>
              +{bills.filter(b => !b.current_cycle_paid).length - 5} more bills
            </span>
          </div>
        )}
      </div>

      {/* ===== 5. Recent Activity ===== */}
      <div className="card">
        <h3>Recent Activity</h3>
        {recentTxns.length === 0 ? (
          <p className="empty-state">
            {lastSync ? 'No transactions this month' : 'Hit sync to pull transactions from Monarch'}
          </p>
        ) : (
          recentTxns.map(txn => {
            const bucket = getCategoryBucket(txn.category);
            return (
              <div key={txn.id} className={`txn-row ${bucket}`}>
                <div className={`txn-dot ${bucket}`} />
                <div className="txn-info">
                  <div className="txn-desc">{txn.description || 'Unknown'}</div>
                  <div className="txn-date">
                    {formatDate(txn.date)} · {txn.category || 'Uncategorized'}
                  </div>
                </div>
                <div className={`txn-amount ${txn.is_credit ? 'income' : 'expense'}`}>
                  {txn.is_credit ? '+' : ''}{formatCurrency(txn.amount)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ===== 6. Sync Status + Refresh Button ===== */}
      <div className="sync-bar">
        <span>{lastSync ? `Updated ${formatDate(lastSync, 'relative')}` : 'Not yet synced'}</span>
        <button
          className="btn btn-sm btn-primary"
          onClick={handleSync}
          disabled={syncing}
          style={{ marginLeft: '0.75rem' }}
        >
          {syncing ? '⟳ Syncing...' : '⟳ Sync'}
        </button>
      </div>
    </div>
  );
}

/**
 * Update the app icon badge with unpaid bill total (rounded to integer dollars).
 * Clears badge when all bills are paid. Graceful no-op on unsupported browsers.
 */
function updateAppBadge(unpaidTotal) {
  if (!('setAppBadge' in navigator)) return;

  const rounded = Math.round(unpaidTotal);
  if (rounded > 0) {
    navigator.setAppBadge(rounded).catch(() => {});
  } else {
    navigator.clearAppBadge().catch(() => {});
  }
}
